import { Button, Card, Space, Typography } from "antd";
import { AD_POSITION } from "@zhihaoscm/meta";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";

import request from "@/api";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useUserStore } from "@/views/stores";

interface AdvertisementContent {
    fileId?: number;
}

interface AdvertisementFile {
    id?: number;
    path?: string;
}

interface AdvertisementPositionRecord {
    id?: number;
    positionType?: number;
    position?: number;
    displayForm?: number;
    content?: AdvertisementContent[];
    firstContent?: AdvertisementContent[];
    secondContent?: AdvertisementContent[];
    displayStructure?: number;
    firstDisplayForm?: number;
    secondDisplayForm?: number;
}

export interface AdvertisementPageProps {
    loadAdvertisements?: () => Promise<AdvertisementPositionRecord[]>;
    loadAdvertisementFile?: (id: number) => Promise<AdvertisementFile>;
    removeAdvertisement?: (id: number) => Promise<unknown>;
    permissions?: string[];
}

const POSITION_TYPES = [
    [10, "首页banner广告位"],
    [1, "首页广告位"],
    [11, "首页最新活动广告位"],
    [2, "查砂价广告位"],
    [3, "查运价广告位"],
    [4, "货找船广告位"],
    [5, "看资讯广告位"],
    [6, "找货源广告位"],
    [7, "找商机广告位"],
    [8, "资源地图广告位"],
    [9, "找资金广告位"]
] as const;

/** 复用 Vue PC 广告位全量接口。 */
export async function loadAdvertisements(): Promise<AdvertisementPositionRecord[]> {
    const response = await request.service.get<AdvertisementPositionRecord[]>(
        "/dataService/admin/advertisement-position/all"
    );
    return response.data;
}

/** 复用 Vue PC 广告位重置接口。 */
export async function removeAdvertisement(id: number): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/advertisement-position/${id}`);
    return response.data;
}

/** 复用 Vue listItemImg 的文件详情接口，补齐广告位首屏图片预览。 */
export async function loadAdvertisementFile(id: number): Promise<AdvertisementFile> {
    const response = await request.service.get<AdvertisementFile>(`/dataService/admin/file/${id}`);
    return response.data;
}

/** 保持 Vue 根据广告位类型选择两种编辑表单的路由规则。 */
function getEditHref(item: AdvertisementPositionRecord): string {
    const editRoute =
        item.positionType === 1 || (item.positionType === 6 && item.position === 2) ? "twoEditType" : "adEdit";
    return `/userOperation/adBanner/${editRoute}?id=${item.id ?? ""}&positionType=${item.positionType ?? ""}`;
}

/** 按旧 Vue listItemImg 的特殊规则生成广告位标题。 */
function getPositionLabel(item: AdvertisementPositionRecord): string {
    const rangeHint = item.position === 1 && item.positionType !== 11 ? " (1-5张)" : "";
    // 找货源广告位沿用 Vue AD_POSITION 的业务名称（如“banner广告位”）。
    const positionLabel =
        item.positionType === 6
            ? AD_POSITION.from(item.position)?.intro || `${item.position ?? "-"}号广告位`
            : `${item.position ?? "-"}号广告位`;
    return `${positionLabel}${rangeHint},建议1张`;
}

/** PC 端广告位首屏，按 Vue positionType 分组并按 position 排序。 */
export function AdvertisementPage({
    loadAdvertisements: load = loadAdvertisements,
    loadAdvertisementFile: loadFile = loadAdvertisementFile,
    removeAdvertisement: remove = removeAdvertisement,
    permissions
}: AdvertisementPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue listItemImg 的 v-auth：无广告写权限时隐藏修改、重置入口。
    const canManage = (permissions ?? storePermissions).includes("ROLE_SS_AD_W");
    const [rows, setRows] = useState<AdvertisementPositionRecord[]>([]);
    const [filePaths, setFilePaths] = useState<Record<number, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        let disposed = false;
        void load()
            .then(async result => {
                if (disposed) return;
                const nextRows = result || [];
                setRows(nextRows);
                const ids = [
                    ...new Set(
                        nextRows
                            .flatMap(item => [
                                ...(item.content || []),
                                ...(item.firstContent || []),
                                ...(item.secondContent || [])
                            ])
                            .map(item => item.fileId)
                            .filter((id): id is number => id != null)
                    )
                ];
                const files = await Promise.all(
                    ids.map(async id => {
                        try {
                            const file = await loadFile(id);
                            return [id, file.path || ""] as const;
                        } catch {
                            return [id, ""] as const;
                        }
                    })
                );
                if (!disposed) setFilePaths(Object.fromEntries(files));
            })
            .catch(() => {
                if (!disposed) setErrorMessage("广告位加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, loadFile, reloadToken]);

    const resetAdvertisement = async (id?: number) => {
        if (id == null) return;
        // Vue confirmable 在重置前要求确认，取消时不应触发删除请求。
        if (!(await confirmDialog("是否重置，重置后用户PC端将展示系统默认图片"))) return;
        try {
            await remove(id);
            notifyOperationSuccess("已重置");
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("广告位重置失败");
        }
    };

    /** 取 Vue 广告位卡片当前展示的第一张图片。 */
    const getImagePath = (item: AdvertisementPositionRecord): string | undefined => {
        const content = [...(item.content || []), ...(item.firstContent || []), ...(item.secondContent || [])];
        const fileId = content.find(entry => entry.fileId)?.fileId;
        return fileId == null ? undefined : filePaths[fileId] || undefined;
    };

    /** 取 Vue 广告位卡片的图片数量和展示状态文案。 */
    const getPositionState = (item: AdvertisementPositionRecord) => {
        const content = [...(item.content || []), ...(item.firstContent || []), ...(item.secondContent || [])];
        const uploadedCount = content.filter(entry => entry.fileId).length;
        const hidden = item.displayForm === 3 || (item.firstDisplayForm === 3 && item.secondDisplayForm === 3);
        return hidden ? "不展示" : `已上传${uploadedCount} 张`;
    };

    /** 按旧 Vue POSITION_TYPE 顺序组织已返回的广告位，空分组不渲染标题。 */
    const groupedRows = POSITION_TYPES.map(([code, title]) => ({
        code,
        title,
        items: rows
            .filter(item => item.positionType === code)
            .sort((left, right) => (left.position || 0) - (right.position || 0))
    })).filter(group => group.items.length > 0);

    return (
        <section aria-label="advertisement-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <div className="advertisement-page-header">
                    <Typography.Title className="advertisement-page-title" level={4}>
                        PC端-首页广告位
                    </Typography.Title>
                    <Typography.Text className="advertisement-page-subtitle">
                        请选择你想投放的首页广告位置
                    </Typography.Text>
                </div>
                {errorMessage ? <Alert className="mt-4" message={errorMessage} role="alert" type="error" /> : null}
                <Space className="advertisement-position-groups" direction="vertical" size={0}>
                    {groupedRows.map(({ code, title, items }) => {
                        return (
                            <section className="advertisement-position-group" key={code} aria-label={title}>
                                <Typography.Title className="advertisement-position-title" level={5}>
                                    {title}
                                </Typography.Title>
                                <div className="advertisement-position-grid">
                                    {items.map(item => (
                                        <div
                                            className="advertisement-position-card"
                                            key={item.id ?? `${code}-${item.position}`}
                                        >
                                            <div className="advertisement-position-image">
                                                {getPositionState(item) === "不展示" ? (
                                                    <div className="advertisement-position-hidden">不展示该广告位</div>
                                                ) : getImagePath(item) ? (
                                                    <img alt={`${title}图片`} src={getImagePath(item)} />
                                                ) : canManage ? (
                                                    <a
                                                        aria-label={`上传轮播图 ${item.position ?? ""}`}
                                                        className="advertisement-position-empty"
                                                        aria-disabled={banButton || undefined}
                                                        href={banButton ? undefined : getEditHref(item)}
                                                    >
                                                        上传轮播图
                                                    </a>
                                                ) : (
                                                    <div className="advertisement-position-empty">上传轮播图</div>
                                                )}
                                            </div>
                                            <div className="advertisement-position-detail">
                                                <div className="advertisement-position-label">
                                                    {getPositionLabel(item)}
                                                </div>
                                                <div className="advertisement-position-count">
                                                    {getPositionState(item)}
                                                </div>
                                                {canManage ? (
                                                    <Space
                                                        className="advertisement-position-actions"
                                                        direction="horizontal"
                                                        size={0}
                                                    >
                                                        <a
                                                            aria-label={`修改广告位 ${item.position}`}
                                                            aria-disabled={banButton || undefined}
                                                            href={getEditHref(item)}
                                                            onClick={event => {
                                                                // 链接没有原生 disabled，封禁态需阻止 Vue 修改入口继续跳转。
                                                                if (banButton) event.preventDefault();
                                                            }}
                                                        >
                                                            <i aria-hidden="true" className="iconfont icon-edit pr-8" />
                                                            修改
                                                        </a>
                                                        <Button
                                                            aria-label={`重置广告位 ${item.position}`}
                                                            disabled={banButton}
                                                            type="link"
                                                            onClick={() => void resetAdvertisement(item.id)}
                                                        >
                                                            <i
                                                                aria-hidden="true"
                                                                className="iconfont icon-a-Frame1312316004 pr-8"
                                                            />
                                                            重置
                                                        </Button>
                                                    </Space>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </Space>
            </Card>
        </section>
    );
}

export default AdvertisementPage;
